/**
 * JPPdfViewerEmbed — JP PMDA supplementary-PDF viewer.
 *
 * Layout: [ PDF page viewer | English side panel ].  The viewer is the
 * shared `RegulatoryViewerEmbed` bound to the `/jp_pdf` family, with JP's
 * block-hover overlay enabled.  Hovering a Japanese block dims the rest
 * of the page, highlights the source block, and streams its English into
 * the side panel — the translation NEVER covers the source (the page
 * image stays the truth).  Mirrors professional CAT-tool / DeepL-doc UX.
 */
import { forwardRef, useImperativeHandle, useRef, useState, useEffect, useCallback, type FC, type ReactNode } from 'react';
import type { ChunkMetadata } from '../../types/viewer';
import type { JpHoverRegion } from './PageRenderer';
import { jpPdfApi } from '../../api/createViewerApi';
import { authApi } from '../../services/authApi';
import RegulatoryViewerEmbed, {
  type ChunkAnchor,
  type RegulatoryViewerHandle,
} from './RegulatoryViewerEmbed';

export type JPPdfViewerHandle = RegulatoryViewerHandle;

/**
 * FormattedBlock — render a flattened block translation with structure,
 * keying off markers that survive translation. Conventions observed
 * across the PMDA doc types:
 *   ●/○        leaflet sub-headings        -> bold sub-heading
 *   ・          bullets                     -> bullet
 *   【…】/〔…〕  rmp/leaflet section titles  -> bold heading
 *   "1. " "(1) " numbered headings (IF/RMP) -> bold lead (needs trailing
 *               space so values like 1.2mg are NOT mis-split)
 * Degrades to plain paragraphs when a block has none, so any doc type is
 * safe. Block boundaries are the unambiguous markers only — numbers are
 * matched at a segment START, never split mid-text.
 */
const _NUM_HEAD = /^(\d+[．.]\s|[（(]\d+[）)]\s|\d+[）)]\s)(.*)$/;

// Wrap every case-insensitive occurrence of ``term`` in ``text`` with a
// <mark> so the searched English word is highlighted in the translation
// panel (so the eye lands on it instead of hunting). Returns the raw string
// untouched when there's no term/match. ``split`` with a capturing group
// alternates [text, match, text, match, …] → odd indices are the matches.
function highlightTerm(text: string, term?: string | null): ReactNode {
  const t = (term ?? '').trim();
  if (!t || !text) return text;
  const esc = t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${esc})`, 'gi'));
  if (parts.length === 1) return text;
  return parts.map((p, i) =>
    i % 2 === 1
      ? <mark key={i} className="bg-yellow-200 rounded-sm px-0.5">{p}</mark>
      : p,
  );
}

const FormattedBlock: FC<{ text: string; highlight?: string | null }> = ({ text, highlight }) => {
  const hl = (s: string): ReactNode => highlightTerm(s, highlight);
  const parts = text
    // The model emits <br> for line breaks; treat them (and literal \n) as breaks
    // so multi-line regions render as lines instead of a run-on with visible <br>.
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/([●○・【〔])/g, '\n$1')
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
  return (
    <div className="space-y-1 text-sm leading-relaxed">
      {parts.map((p, i) => {
        if (p[0] === '●' || p[0] === '○') {
          const body = p.replace(/^[●○]\s*/, '');
          const m = body.match(/^(.{2,48}?[:：])\s*([\s\S]*)$/);
          return m ? (
            <p key={i} className="mt-2 text-slate-800">
              <span className="font-semibold">{hl(m[1] ?? '')}</span> {hl(m[2] ?? '')}
            </p>
          ) : (
            <p key={i} className="mt-2 font-semibold text-slate-800">{hl(body)}</p>
          );
        }
        if (p[0] === '【' || p[0] === '〔') {
          return <p key={i} className="mt-2 font-semibold text-slate-800">{hl(p)}</p>;
        }
        if (p[0] === '・') {
          return <p key={i} className="pl-3 text-slate-700">• {hl(p.replace(/^・\s*/, ''))}</p>;
        }
        const nm = p.match(_NUM_HEAD);
        if (nm) {
          return (
            <p key={i} className="mt-1.5 text-slate-800">
              <span className="font-semibold">{(nm[1] ?? '').trim()}</span> {hl(nm[2] ?? '')}
            </p>
          );
        }
        return <p key={i} className="text-slate-700">{hl(p)}</p>;
      })}
    </div>
  );
};

/**
 * LinkedTables — render the translated grid and the Japanese source grid one
 * above the other, with SYNCHRONIZED cell highlighting: hovering a cell in
 * either table highlights the SAME logical cell in both.  The two tables share
 * an identical cell structure (the backend's cell-wise translation rebuilds the
 * grid from the source's cells, so the Nth cell of each is the same logical
 * cell), so a single reading-order index links them.  This is the "highlight
 * the translation as in the original" the reviewer asked for.
 */
type Cell = { tag: 'td' | 'th'; text: string; colspan: number };

function parseGrid(html: string): Cell[][] {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return [...doc.querySelectorAll('tr')].map((tr) =>
      [...tr.children].map((td) => ({
        tag: (td.tagName.toLowerCase() === 'th' ? 'th' : 'td') as 'td' | 'th',
        text: td.textContent || '',
        colspan: parseInt(td.getAttribute('colspan') || '1', 10) || 1,
      })),
    );
  } catch {
    return [];
  }
}

const LinkedTables: FC<{ enHtml: string; jaHtml: string; highlight?: string | null }> = ({
  enHtml,
  jaHtml,
  highlight,
}) => {
  const [hover, setHover] = useState<number | null>(null);
  const en = parseGrid(enHtml);
  const ja = parseGrid(jaHtml);
  // The two grids must share the same cell count for index-linking to be
  // valid; if a translation fell back to the generic path and diverged, drop
  // the linkage (still render the translated grid, just unlinked).
  const enCount = en.reduce((n, r) => n + r.length, 0);
  const jaCount = ja.reduce((n, r) => n + r.length, 0);
  const linked = enCount > 0 && enCount === jaCount;

  // ``hl`` applies the search-term <mark> to cell text (English grid only — the
  // searched term is English; the Japanese source grid stays plain). Without it,
  // a term that matched the table in search was invisible in the rendered grid.
  const renderGrid = (rows: Cell[][], hl?: string | null) => {
    let idx = -1;
    return (
      <table>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri}>
              {r.map((c, ci) => {
                idx += 1;
                const my = idx;
                const Tag = c.tag;
                const on = linked && hover === my;
                return (
                  <Tag
                    key={ci}
                    colSpan={c.colspan}
                    onMouseEnter={linked ? () => setHover(my) : undefined}
                    style={on ? { background: 'rgba(16,185,129,0.28)' } : undefined}
                  >
                    {hl ? highlightTerm(c.text, hl) : c.text}
                  </Tag>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div onMouseLeave={() => setHover(null)} className="space-y-2">
      <div className="jp-pdf-table text-sm text-slate-800">{renderGrid(en, highlight)}</div>
      <details open className="mt-1">
        <summary className="text-[11px] text-slate-400 cursor-pointer">
          日本語 (source){linked ? ' — hover to link cells' : ''}
        </summary>
        <div className="jp-pdf-table mt-1 text-xs text-slate-500">{renderGrid(ja)}</div>
      </details>
    </div>
  );
};

interface JPPdfViewerEmbedProps {
  docId: number;
  highlightChunkIds?: number[];
  chunkAnchors?: Record<number, ChunkAnchor[]>;
  /** page_num -> anchor for a chunk-free (progressive-read) citation.
   *  Forwarded verbatim to RegulatoryViewerEmbed via the props spread. */
  pageAnchors?: Record<number, ChunkAnchor[]> | undefined;
  landingPage?: number | undefined;
  highlightTerm?: string | null;
  onChunkClick?: (chunk: ChunkMetadata) => void;
}

const JPPdfViewerEmbed = forwardRef<JPPdfViewerHandle, JPPdfViewerEmbedProps>(
  function JPPdfViewerEmbed(props, ref) {
    const innerRef = useRef<RegulatoryViewerHandle>(null);
    const [region, setRegion] = useState<JpHoverRegion | null>(null);
    // Keep the last hovered region visible after the cursor leaves, so the
    // reader can move to the panel to read — only replaced by the next hover.
    const onBlockHover = useCallback((r: JpHoverRegion | null) => {
      if (r) setRegion(r);
    }, []);

    // Active in-document search word, forwarded from the inner viewer's search
    // box. The page layer is Japanese and the side panel is English — the SAME
    // query must NOT be highlighted on both surfaces at once, because
    // translation is not occurrence-preserving: 尿路上皮癌 appears 6× in the
    // Japanese source but the condensed English translation says "urothelial
    // carcinoma" only 3×, so highlighting both showed mismatched counts (6 vs
    // 3) that looked broken. Instead we AUTO-DETECT the query's script and
    // highlight exactly ONE surface:
    //   • Japanese query (かな/カナ/漢字) → boxed directly on the Japanese page
    //     (a 1:1 substring match — honest, no translation step).
    //   • English query → highlighted in the English panel only; the page is
    //     left un-boxed (we no longer translate the term to Japanese, which is
    //     the step that produced the mismatched counts).
    const [enTerm, setEnTerm] = useState<string | null>(null);
    const queryIsJapanese =
      !!enTerm && /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]/.test(enTerm);
    // Page (Japanese) highlight — only when the user typed Japanese.
    const pageHighlightTerms = queryIsJapanese ? [enTerm!.trim()] : null;
    // English-panel highlight — only when the user typed English.
    const panelHighlight = enTerm && !queryIsJapanese ? enTerm : null;

    // Canonical section-heading titles, keyed by their normalized Japanese
    // heading.  When the hovered block IS a section heading, we show this
    // stored ``title`` (the exact string the Contents TOC renders) instead
    // of the panel's independent live re-translation — so the TOC header and
    // the third-panel header never read differently for the same heading
    // (e.g. "What is this drug?" vs "What is this medicine?").  Body blocks
    // are unaffected (they aren't headings, so they miss the map and keep
    // their live translation).
    const [headingTitleByJa, setHeadingTitleByJa] = useState<Map<string, string>>(new Map());
    useEffect(() => {
      let cancelled = false;
      jpPdfApi.fetchTOC(props.docId).then((toc) => {
        if (cancelled) return;
        const m = new Map<string, string>();
        for (const s of toc.sections ?? []) {
          const ja = (s.title_ja ?? '').replace(/\s+/g, '');
          if (ja) m.set(ja, s.title);
        }
        setHeadingTitleByJa(m);
      }).catch(() => { /* TOC fetch best-effort; panel still works */ });
      return () => { cancelled = true; };
    }, [props.docId]);

    // Resolve the English to show for the hovered region: a section heading
    // maps to its canonical TOC title; everything else uses the live
    // translation.  Headings are short single-line 【…】/〔…〕 blocks, so the
    // whitespace-stripped JA matches a TOC ``title_ja`` exactly.
    const resolveRegionEn = useCallback((r: JpHoverRegion): string | undefined => {
      if (r.isHtml) return r.en; // tables never carry a heading title
      const key = (r.ja ?? '').replace(/\s+/g, '');
      const canonical = headingTitleByJa.get(key);
      return canonical ?? r.en;
    }, [headingTitleByJa]);

    // Resizable English panel — drag the left edge. Clamped 280–760px.
    const [panelW, setPanelW] = useState(384);
    const dragging = useRef(false);
    // Delta-based drag: snapshot the cursor x and panel width at mousedown,
    // then resize by the cursor's MOVEMENT, not by an absolute
    // cursor-to-right-edge measurement. The old absolute formula
    // (``containerRight - clientX``) snapped the panel to its 280px min on
    // laptops / when the CEPI sibling panel skews the embed's right edge —
    // and once collapsed it couldn't be dragged back out. The delta is
    // layout-independent so it works on any screen and in both directions.
    const dragStartX = useRef(0);
    const dragStartW = useRef(384);
    const containerRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      const onMove = (e: MouseEvent) => {
        if (!dragging.current) return;
        // Dragging LEFT (clientX decreases) widens the panel.
        const delta = dragStartX.current - e.clientX;
        setPanelW(Math.min(760, Math.max(280, dragStartW.current + delta)));
      };
      const onUp = () => {
        dragging.current = false;
        document.body.style.userSelect = '';
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      return () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
    }, []);

    useImperativeHandle(ref, () => ({
      scrollToPage: (n) => innerRef.current?.scrollToPage(n),
      highlightChunks: (ids) => innerRef.current?.highlightChunks(ids),
      scrollToSection: (s) => innerRef.current?.scrollToSection(s) ?? false,
    }));

    return (
      <div ref={containerRef} className="flex h-full min-h-0">
        <style>{`
          .jp-pdf-table table { border-collapse: collapse; width: 100%; font-size: 0.78rem; }
          .jp-pdf-table th, .jp-pdf-table td {
            border: 1px solid #e2e8f0; padding: 3px 6px; text-align: left; vertical-align: top;
          }
          .jp-pdf-table th { background: #f8fafc; font-weight: 600; }
        `}</style>
        <div className="flex-1 min-h-0">
          <RegulatoryViewerEmbed
            ref={innerRef}
            viewerApi={jpPdfApi}
            blockHoverTranslate={(ja, preserveHtml, sectionId) =>
              authApi.translateJpPdfText(ja, preserveHtml, props.docId, sectionId)}
            onBlockHover={onBlockHover}
            {...props}
            onActiveSearchTermChange={setEnTerm}
            // Box the searched word on the page ONLY when the user typed
            // Japanese (the page text layer is Japanese, so it's a direct 1:1
            // match). English queries leave the page un-boxed and highlight the
            // English panel instead — see queryIsJapanese above.
            highlightTerms={pageHighlightTerms}
          />
        </div>

        {/* Drag handle to resize the English panel */}
        <div
          onMouseDown={(e) => {
            dragging.current = true;
            dragStartX.current = e.clientX;
            dragStartW.current = panelW;
            document.body.style.userSelect = 'none';
            e.preventDefault();
          }}
          className="w-1.5 flex-shrink-0 cursor-col-resize bg-slate-200 hover:bg-indigo-300 transition-colors"
          title="Drag to resize"
        />

        {/* English side panel — updates on hover, never covers the page */}
        <aside
          style={{ width: panelW }}
          className="flex-shrink-0 border-l border-slate-200 bg-white flex flex-col min-h-0">
          <div className="px-3 py-2 border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-400 flex items-center justify-between">
            <span>English</span>
            <span className="text-slate-300 normal-case">hover the Japanese text</span>
          </div>
          {enTerm && (
            <div className="px-3 py-1.5 text-[11px] normal-case text-slate-500 bg-slate-50 border-b border-slate-100">
              {queryIsJapanese
                ? <>Japanese query — highlighted on the page</>
                : <>English query — highlighted here, not on the page (JP↔EN counts differ)</>}
            </div>
          )}
          <div className="flex-1 min-h-0 overflow-auto p-3">
            {region == null ? (
              <div className="text-sm text-slate-400 italic mt-6 text-center">
                Hover any Japanese paragraph on the left to see its English here.
              </div>
            ) : (
              <div>
                {region.kind === 'table' && (
                  <div className="text-[10px] uppercase tracking-wide text-emerald-600 mb-1">Table</div>
                )}
                {region.en === undefined ? (
                  <div className="text-sm text-slate-400 italic">Translating…</div>
                ) : region.isHtml ? (
                  // Tables: linked translated + source grids with synced cell hover.
                  <LinkedTables enHtml={region.en || ''} jaHtml={region.ja} highlight={panelHighlight} />
                ) : (
                  <>
                    <FormattedBlock text={resolveRegionEn(region) || '(no translation)'} highlight={panelHighlight} />
                    <details className="mt-3">
                      <summary className="text-[11px] text-slate-400 cursor-pointer">日本語 (source)</summary>
                      <div className="mt-1 text-xs text-slate-500">
                        <FormattedBlock text={region.ja} />
                      </div>
                    </details>
                  </>
                )}
              </div>
            )}
          </div>
        </aside>
      </div>
    );
  },
);

export default JPPdfViewerEmbed;
